import { CityAttractionRO } from '@escapenavigator/types/dist/city-attraction/city-attraction.ro';
import { AttractionCategoryEnum } from '@escapenavigator/types/dist/city-attraction/enum/attraction-category.enum';
import { AttractionFameEnum } from '@escapenavigator/types/dist/city-attraction/enum/attraction-fame.enum';
import { AttractionIndoorOutdoorEnum } from '@escapenavigator/types/dist/city-attraction/enum/attraction-indoor-outdoor.enum';
import { AttractionRouteRoleEnum } from '@escapenavigator/types/dist/city-attraction/enum/attraction-route-role.enum';
import {
    Alert,
    Button,
    Group,
    Modal,
    NumberInput,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { api } from '../api';
import { apiErrorMessage } from '../api/client';
import { t } from '../dictionary';
import {
    categoryOptions,
    fameOptions,
    indoorOutdoorOptions,
    routeRoleOptions,
} from '../utils/attraction-labels';

type Props = {
    opened: boolean;
    onClose: () => void;
    cityId: number;
    onCreated: (attraction: CityAttractionRO) => void;
};

export const AttractionFormModal = ({ opened, onClose, cityId, onCreated }: Props) => {
    const [saving, setSaving] = useState(false);
    const [duplicates, setDuplicates] = useState<CityAttractionRO[]>([]);

    const form = useForm({
        initialValues: {
            title: '',
            description: '',
            knownFor: '',
            knownForRu: '',
            category: AttractionCategoryEnum.ARCHITECTURE_LANDMARK as string,
            fame: AttractionFameEnum.WELL_KNOWN as string,
            routeRole: '' as string,
            indoorOutdoor: '' as string,
            address: '',
            district: '',
            lat: '' as string | number,
            lng: '' as string | number,
        },
        validate: {
            title: (v) => (v.trim() ? null : 'Enter a title'),
            category: (v) => (v ? null : 'Choose a category'),
            fame: (v) => (v ? null : 'Choose fame'),
            lat: (v) =>
                v === '' || v == null || Number.isNaN(Number(v)) ? 'Latitude is required' : null,
            lng: (v) =>
                v === '' || v == null || Number.isNaN(Number(v)) ? 'Longitude is required' : null,
        },
    });

    useEffect(() => {
        if (!opened) {
            form.reset();
            setDuplicates([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened]);

    const { title, lat, lng } = form.values;

    useEffect(() => {
        if (!opened || !cityId) return;

        const latN = Number(lat);
        const lngN = Number(lng);

        if (!Number.isFinite(latN) || !Number.isFinite(lngN)) {
            setDuplicates([]);

            return;
        }

        const timer = setTimeout(() => {
            api.attractionDuplicates({
                cityId,
                lat: latN,
                lng: lngN,
                title: title.trim() || undefined,
            })
                .then(({ data }) => setDuplicates(data))
                .catch(() => setDuplicates([]));
        }, 400);

        return () => clearTimeout(timer);
    }, [opened, cityId, title, lat, lng]);

    const submit = async () => {
        if (form.validate().hasErrors) return;

        setSaving(true);

        try {
            const values = form.values;
            const { data } = await api.createAttraction({
                title: values.title.trim(),
                description: values.description.trim() || null,
                knownFor: values.knownFor.trim() || null,
                knownForRu: values.knownForRu.trim() || null,
                category: values.category as AttractionCategoryEnum,
                fame: values.fame as AttractionFameEnum,
                cityId,
                address: values.address.trim() || null,
                district: values.district.trim() || null,
                lat: Number(values.lat),
                lng: Number(values.lng),
                routeRole: (values.routeRole || null) as AttractionRouteRoleEnum | null,
                indoorOutdoor: (values.indoorOutdoor || null) as AttractionIndoorOutdoorEnum | null,
            });

            notifications.show({ color: 'green', message: t.stories.attractionSaved });
            onCreated(data);
            onClose();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t.stories.attractionCreateTitle}
            size='lg'
            centered
        >
            <Stack gap='md'>
                <TextInput
                    label={t.stories.attractionTitle}
                    placeholder={t.stories.attractionTitlePlaceholder}
                    required
                    {...form.getInputProps('title')}
                />

                <Textarea
                    label={t.stories.attractionDescription}
                    placeholder={t.stories.attractionDescriptionPlaceholder}
                    minRows={2}
                    autosize
                    {...form.getInputProps('description')}
                />

                <Group grow>
                    <Textarea
                        label={t.stories.attractionKnownFor}
                        placeholder={t.stories.attractionKnownForPlaceholder}
                        minRows={2}
                        autosize
                        {...form.getInputProps('knownFor')}
                    />
                    <Textarea
                        label={t.stories.attractionKnownForRu}
                        placeholder={t.stories.attractionKnownForPlaceholder}
                        minRows={2}
                        autosize
                        {...form.getInputProps('knownForRu')}
                    />
                </Group>

                <Group grow>
                    <Select
                        label={t.stories.attractionCategory}
                        data={categoryOptions}
                        searchable
                        required
                        {...form.getInputProps('category')}
                    />
                    <Select
                        label={t.stories.attractionFame}
                        data={fameOptions}
                        required
                        {...form.getInputProps('fame')}
                    />
                </Group>

                <Group grow>
                    <Select
                        label={t.stories.attractionRouteRole}
                        data={routeRoleOptions}
                        clearable
                        {...form.getInputProps('routeRole')}
                    />
                    <Select
                        label={t.stories.attractionIndoorOutdoor}
                        data={indoorOutdoorOptions}
                        clearable
                        {...form.getInputProps('indoorOutdoor')}
                    />
                </Group>

                <Group grow>
                    <TextInput
                        label={t.stories.attractionAddress}
                        placeholder={t.stories.attractionAddressPlaceholder}
                        {...form.getInputProps('address')}
                    />
                    <TextInput
                        label={t.stories.attractionDistrict}
                        placeholder={t.stories.attractionDistrictPlaceholder}
                        {...form.getInputProps('district')}
                    />
                </Group>

                <Group grow>
                    <NumberInput
                        label={t.stories.attractionLat}
                        required
                        decimalScale={7}
                        {...form.getInputProps('lat')}
                    />
                    <NumberInput
                        label={t.stories.attractionLng}
                        required
                        decimalScale={7}
                        {...form.getInputProps('lng')}
                    />
                </Group>

                {duplicates.length > 0 && (
                    <Alert variant='light' color='yellow' icon={<IconAlertTriangle />}>
                        <Text size='sm' fw={600} mb={4}>
                            {t.stories.attractionDuplicatesWarning}
                        </Text>
                        <Stack gap={4}>
                            {duplicates.slice(0, 5).map((d) => (
                                <Text key={d.id} size='sm'>
                                    {d.title}
                                    {d.district ? ` · ${d.district}` : ''}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                <Group justify='flex-end'>
                    <Button variant='default' onClick={onClose}>
                        {t.common.cancel}
                    </Button>
                    <Button loading={saving} onClick={submit}>
                        {t.stories.createAttraction}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
